import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Tooltip,
  CircularProgress,
  Alert,
  Collapse,
  Pagination
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as ResolveIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  BugReport as CriticalIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { backendApiService } from '../../services/backendApiService';

interface ErrorLog {
  id: string;
  error_type: string;
  error_code: string;
  error_message: string;
  error_stack: string;
  user_email: string;
  page_url: string;
  api_endpoint: string;
  request_method: string;
  request_body: any;
  severity: 'error' | 'warning' | 'critical';
  component: string;
  browser: string;
  browser_version: string;
  os: string;
  device_type: string;
  screen_resolution: string;
  additional_context: any;
  occurred_at: string;
  resolved: boolean;
  resolved_at: string;
  resolved_by: string;
}

interface ErrorSummary {
  total_errors: number;
  by_severity: {
    critical: number;
    error: number;
    warning: number;
  };
  by_type: Record<string, number>;
  recent_24h: number;
  top_endpoints: Array<{ endpoint: string; count: number }>;
}

const severityColors = {
  critical: '#dc3545',
  error: '#fd7e14',
  warning: '#ffc107'
};

const severityIcons = {
  critical: <CriticalIcon />,
  error: <ErrorIcon />,
  warning: <WarningIcon />
};

export const ErrorDashboardPage: React.FC = () => {
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [summary, setSummary] = useState<ErrorSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Filters
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showResolved, setShowResolved] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  // Test digest state
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestMessage, setDigestMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      // Fetch summary
      const summaryResponse = await backendApiService.get('/api/errors/summary');
      if (summaryResponse.success) {
        setSummary(summaryResponse.data);
      }

      // Fetch recent errors
      const errorsResponse = await backendApiService.get('/api/errors/recent', {
        limit: pageSize,
        offset: (page - 1) * pageSize
      });
      if (errorsResponse.success) {
        setErrors(errorsResponse.data.errors || []);
        setTotalPages(Math.ceil((errorsResponse.data.total || 0) / pageSize));
      }
    } catch (err) {
      console.error('Failed to fetch error data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page]);

  const handleResolve = async (errorId: string) => {
    try {
      await backendApiService.put(`/api/errors/${errorId}/resolve`, {});
      // Refresh data
      fetchData(true);
    } catch (err) {
      console.error('Failed to resolve error:', err);
    }
  };

  const handleSendTestDigest = async () => {
    setSendingDigest(true);
    setDigestMessage(null);

    try {
      const response = await backendApiService.post('/api/email-reports/error-digest/test', {
        email: 'james@codeops.nl'
      });

      if (response.success) {
        setDigestMessage({ type: 'success', text: 'Test digest sent successfully!' });
      } else {
        setDigestMessage({ type: 'error', text: response.error || 'Failed to send digest' });
      }
    } catch (err: any) {
      setDigestMessage({ type: 'error', text: err.message || 'Failed to send digest' });
    } finally {
      setSendingDigest(false);
    }
  };

  const filteredErrors = errors.filter(error => {
    if (severityFilter !== 'all' && error.severity !== severityFilter) return false;
    if (typeFilter !== 'all' && error.error_type !== typeFilter) return false;
    if (!showResolved && error.resolved) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        error.error_message?.toLowerCase().includes(search) ||
        error.api_endpoint?.toLowerCase().includes(search) ||
        error.page_url?.toLowerCase().includes(search) ||
        error.user_email?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Error Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={sendingDigest ? <CircularProgress size={20} /> : <EmailIcon />}
            onClick={handleSendTestDigest}
            disabled={sendingDigest}
          >
            Send Test Digest
          </Button>
          <Button
            variant="contained"
            startIcon={refreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={() => fetchData(true)}
            disabled={refreshing}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {digestMessage && (
        <Alert severity={digestMessage.type} sx={{ mb: 2 }} onClose={() => setDigestMessage(null)}>
          {digestMessage.text}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {summary?.total_errors || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Total Errors
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: severityColors.critical }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {summary?.by_severity?.critical || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Critical
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: severityColors.error }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {summary?.by_severity?.error || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Errors
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: severityColors.warning }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {summary?.by_severity?.warning || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Warnings
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Failing Endpoints */}
      {summary?.top_endpoints && summary.top_endpoints.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Top Failing Endpoints</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {summary.top_endpoints.map((ep, idx) => (
                <Chip
                  key={idx}
                  label={`${ep.endpoint} (${ep.count})`}
                  color={idx === 0 ? 'error' : 'default'}
                  variant={idx === 0 ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search errors..."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Severity</InputLabel>
                <Select
                  value={severityFilter}
                  label="Severity"
                  onChange={(e) => setSeverityFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="error">Error</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="api_error">API Error</MenuItem>
                  <MenuItem value="js_error">JS Error</MenuItem>
                  <MenuItem value="network">Network</MenuItem>
                  <MenuItem value="timeout">Timeout</MenuItem>
                  <MenuItem value="unhandled_rejection">Unhandled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Button
                variant={showResolved ? 'contained' : 'outlined'}
                onClick={() => setShowResolved(!showResolved)}
                fullWidth
              >
                {showResolved ? 'Hide Resolved' : 'Show Resolved'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#f5f5f5' }}>
              <TableCell width={40}></TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Endpoint/Page</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Time</TableCell>
              <TableCell width={80}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredErrors.map((error) => (
              <React.Fragment key={error.id}>
                <TableRow
                  hover
                  sx={{
                    opacity: error.resolved ? 0.5 : 1,
                    '&:hover': { cursor: 'pointer' }
                  }}
                  onClick={() => setExpandedRow(expandedRow === error.id ? null : error.id)}
                >
                  <TableCell>
                    <IconButton size="small">
                      {expandedRow === error.id ? <CollapseIcon /> : <ExpandIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={severityIcons[error.severity]}
                      label={error.severity}
                      size="small"
                      sx={{
                        background: severityColors[error.severity],
                        color: 'white',
                        '& .MuiChip-icon': { color: 'white' }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={error.error_type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" noWrap title={error.error_message}>
                      {error.error_message?.substring(0, 60)}
                      {error.error_message?.length > 60 ? '...' : ''}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap>
                      {error.api_endpoint || error.page_url || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {error.user_email || 'Anonymous'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(error.occurred_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {!error.resolved && (
                      <Tooltip title="Mark as Resolved">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResolve(error.id);
                          }}
                        >
                          <ResolveIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {error.resolved && (
                      <Chip label="Resolved" size="small" color="success" />
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={8} sx={{ p: 0 }}>
                    <Collapse in={expandedRow === error.id}>
                      <Box sx={{ p: 2, background: '#fafafa' }}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle2" color="textSecondary">Full Message</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                              {error.error_message}
                            </Typography>

                            {error.error_stack && (
                              <>
                                <Typography variant="subtitle2" color="textSecondary">Stack Trace</Typography>
                                <Box sx={{
                                  background: '#1e1e1e',
                                  color: '#d4d4d4',
                                  p: 1,
                                  borderRadius: 1,
                                  fontSize: '11px',
                                  fontFamily: 'monospace',
                                  maxHeight: 200,
                                  overflow: 'auto'
                                }}>
                                  <pre style={{ margin: 0 }}>{error.error_stack}</pre>
                                </Box>
                              </>
                            )}
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle2" color="textSecondary">Details</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                              <Typography variant="body2"><strong>Browser:</strong> {error.browser} {error.browser_version}</Typography>
                              <Typography variant="body2"><strong>OS:</strong> {error.os}</Typography>
                              <Typography variant="body2"><strong>Device:</strong> {error.device_type}</Typography>
                              <Typography variant="body2"><strong>Screen:</strong> {error.screen_resolution}</Typography>
                              <Typography variant="body2"><strong>Method:</strong> {error.request_method || '-'}</Typography>
                              <Typography variant="body2"><strong>Code:</strong> {error.error_code || '-'}</Typography>
                            </Box>

                            {error.request_body && (
                              <>
                                <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Request Body</Typography>
                                <Box sx={{
                                  background: '#f5f5f5',
                                  p: 1,
                                  borderRadius: 1,
                                  fontSize: '11px',
                                  fontFamily: 'monospace',
                                  maxHeight: 100,
                                  overflow: 'auto'
                                }}>
                                  <pre style={{ margin: 0 }}>{JSON.stringify(error.request_body, null, 2)}</pre>
                                </Box>
                              </>
                            )}

                            {error.additional_context && (
                              <>
                                <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Additional Context</Typography>
                                <Box sx={{
                                  background: '#f5f5f5',
                                  p: 1,
                                  borderRadius: 1,
                                  fontSize: '11px',
                                  fontFamily: 'monospace',
                                  maxHeight: 100,
                                  overflow: 'auto'
                                }}>
                                  <pre style={{ margin: 0 }}>{JSON.stringify(error.additional_context, null, 2)}</pre>
                                </Box>
                              </>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      )}

      {/* Empty State */}
      {filteredErrors.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>No errors found</Typography>
          <Typography variant="body2" color="textSecondary">
            {errors.length === 0
              ? "Your unicorn is running perfectly!"
              : "Try adjusting your filters"}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ErrorDashboardPage;
